import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Bug, Check, Loader2, X } from 'lucide-react';
import {
  bugReportProblem,
  bugSeverities,
  bugSeverityLabels,
  bugStatusLabels,
  currentBugContext,
  type BugReportGateway,
  type BugSeverity,
  type OwnBugReport,
} from '@forq/platform-data';

interface BugReportDialogProps {
  readonly open: boolean;
  readonly tenantId: string;
  readonly gateway: BugReportGateway;
  readonly onClose: () => void;
}

/**
 * „Fehler melden" — der Rückkanal der Alphaphase.
 *
 * Zwei Entscheidungen prägen die Maske:
 *
 * 1. **Der Kontext steht sichtbar da.** Seite, Fassung, Browser und
 *    Fenstergröße gehen mit — und man sieht, dass sie mitgehen. Etwas
 *    heimlich mitzuschicken wäre der einfachere Weg und der falsche.
 *
 * 2. **Die eigenen Meldungen stehen darunter, mit Stand.** Ohne das fragt
 *    derselbe Kollege in zwei Wochen nach, ob es angekommen ist — und meldet
 *    sicherheitshalber noch einmal.
 */
export function BugReportDialog({ open, tenantId, gateway, onClose }: BugReportDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('stoerend');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [mine, setMine] = useState<readonly OwnBugReport[]>([]);

  // Beim Öffnen festhalten, WO der Melder stand -- nicht beim Absenden. Wer
  // erst tippt und dabei die Seite wechselt, meldet sonst die falsche.
  const [context, setContext] = useState(() => currentBugContext(__FORQ_BUILD__));

  const ownReports = useCallback(() => {
    gateway.listMine(tenantId).then(setMine).catch(() => setMine([]));
  }, [gateway, tenantId]);

  useEffect(() => {
    if (!open) return;
    setContext(currentBugContext(__FORQ_BUILD__));
    setSent(false);
    setError(null);
    ownReports();
  }, [open, ownReports]);

  useEffect(() => {
    if (!open) return;
    const beiEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', beiEscape);
    return () => window.removeEventListener('keydown', beiEscape);
  }, [open, onClose]);

  if (!open) return null;

  async function absenden(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const eingabe = { title, description, severity };
    const problem = bugReportProblem(eingabe);
    if (problem) { setError(problem); return; }

    setBusy(true);
    setError(null);
    try {
      await gateway.report(tenantId, eingabe, context);
      setTitle('');
      setDescription('');
      setSeverity('stoerend');
      setSent(true);
      ownReports();
    } catch (fehler: unknown) {
      setError(fehler instanceof Error && fehler.message
        ? fehler.message
        : 'Die Meldung konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="forq-bug-backdrop" role="presentation" onClick={onClose}>
      <div
        className="forq-bug"
        role="dialog"
        aria-modal="true"
        aria-labelledby="forq-bug-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="forq-eyebrow">Alphaphase</span>
            <h2 id="forq-bug-title"><Bug size={18} /> Fehler melden</h2>
          </div>
          <button className="forq-bug__close" type="button" aria-label="Schließen" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <form onSubmit={absenden}>
          <label>
            Was ist passiert?
            <input
              type="text"
              value={title}
              maxLength={160}
              autoFocus
              placeholder="z. B. Angebot lässt sich nicht drucken"
              onChange={(event) => { setTitle(event.target.value); setSent(false); }}
            />
          </label>

          <label>
            Beschreibung — was hast du getan, was hast du erwartet?
            <textarea
              value={description}
              rows={5}
              maxLength={4000}
              placeholder={'Ich wollte …\nErwartet hätte ich …\nStattdessen …'}
              onChange={(event) => { setDescription(event.target.value); setSent(false); }}
            />
          </label>

          <fieldset className="forq-bug__severity">
            <legend>Wie schlimm ist es?</legend>
            {bugSeverities.map((stufe) => (
              <label key={stufe}>
                <input
                  type="radio"
                  name="severity"
                  value={stufe}
                  checked={severity === stufe}
                  onChange={() => setSeverity(stufe)}
                />
                <span>{bugSeverityLabels[stufe]}</span>
              </label>
            ))}
          </fieldset>

          {/* Offen zeigen, was mitgeht. */}
          <div className="forq-bug__context">
            <span className="forq-eyebrow">Geht automatisch mit</span>
            <dl>
              <div><dt>Seite</dt><dd>{context.route || '–'}</dd></div>
              <div><dt>Fassung</dt><dd>{context.appVersion || '–'}</dd></div>
              <div><dt>Fenster</dt><dd>{context.viewport || '–'}</dd></div>
            </dl>
          </div>

          {error && <p className="forq-bug__error" role="alert">{error}</p>}
          {sent && !error && (
            <p className="forq-bug__sent" role="status">
              <Check size={15} /> Angekommen. Danke — du siehst den Stand unten.
            </p>
          )}

          <div className="forq-bug__actions">
            <button type="button" className="forq-bug__secondary" onClick={onClose}>Schließen</button>
            <button type="submit" className="forq-bug__primary" disabled={busy}>
              {busy ? <><Loader2 size={15} className="forq-spin" /> Wird gesendet …</> : 'Melden'}
            </button>
          </div>
        </form>

        <section className="forq-bug__mine" aria-label="Meine Meldungen">
          <span className="forq-eyebrow">Meine Meldungen</span>
          {mine.length === 0 ? (
            <p className="forq-bug__empty">Noch nichts gemeldet.</p>
          ) : (
            <ul>
              {mine.slice(0, 8).map((eintrag) => (
                <li key={eintrag.id}>
                  <span className={`forq-bug__status is-${eintrag.status}`}>{bugStatusLabels[eintrag.status]}</span>
                  <div>
                    <strong>{eintrag.title}</strong>
                    {eintrag.resolutionNote && <small>{eintrag.resolutionNote}</small>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
